import { addServiceWorks, getServiceWorks, getShutdownHours, removeServiceWorks, updateServiceWorks } from 'lib/communication/admin';
import { getFormattedDateWithHoursAndMinutes } from 'lib/util';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Table, Form, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { AppState } from 'reducers';
import '../Admin.scss';
import { FaRegTrashAlt, FaPlus, FaEdit, FaCheck } from 'react-icons/fa';
import DatePicker from 'components/DatePicker/DatePicker';
import ButtonComponent from 'components/ButtonComponent';
import { ServiceWorkType } from 'lib/types';
import { useDevice } from 'lib/hooks/useDevice';

const ServiceWorks = () => {
    const { isDesktop } = useDevice();
    const shutdownHours = useSelector((state: AppState) => state.data.adminInfo.shutdownHours);
    const serviceWorks = useSelector((state: AppState) => state.data.adminInfo.serviceWorks);
    const serviceWorkFetching = useSelector((state: AppState) => state.data.adminInfo.props.isFetching);
    const [dateTimeFrom, setDateTimeFrom] = useState<Date | null>(null);
    const [dateTimeTo, setDateTimeTo] = useState<Date | null>(null);
    const [serviceWorkMessage, setServiceWorkMessage] = useState<string>('');
    const [dateTimeFromEdit, setDateTimeFromEdit] = useState<Date | null>(null);
    const [dateTimeToEdit, setDateTimeToEdit] = useState<Date | null>(null);
    const [serviceWorkMessageEdit, setServiceWorkMessageEdit] = useState<string>('');
    const [invalidDates, setInvalidDates] = useState<boolean>(false);
    const [invalidDatesEdit, setInvalidDatesEdit] = useState<boolean>(false);
    const [invalidMessage, setInvalidMessage] = useState<boolean>(false);
    const [invalidMessageEdit, setInvalidMessageEdit] = useState<boolean>(false);
    const [editedServiceWork, setEditedServiceWork] = useState<ServiceWorkType | null>(null);

    useEffect(() => {
        getServiceWorks();
        getShutdownHours();
    }, []);

    const setEditedProperties = (sw: ServiceWorkType) => {
        setEditedServiceWork(sw);
        setDateTimeFromEdit(sw.from);
        setDateTimeToEdit(sw.to);
        setServiceWorkMessageEdit(sw.message);
    };

    const convertDate = (originalDate: Date) => originalDate.toLocaleString([], {
        hour: '2-digit',
        minute: '2-digit',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    const editServiceWorks = async (sw: ServiceWorkType) => {
        setInvalidDatesEdit(false);
        const { id, from, to, message } = sw;
        if (dateTimeFromEdit === from && dateTimeToEdit === to && serviceWorkMessageEdit === message) {
            setEditedServiceWork(null);
            return;
        }

        if (!(dateTimeFromEdit && dateTimeToEdit)) {
            setInvalidDatesEdit(true);
            return;
        }

        if (!(dateTimeToEdit && dateTimeFromEdit) || dateTimeToEdit <= dateTimeFromEdit) {
            setInvalidDatesEdit(true);
            return;
        }

        if (!serviceWorkMessageEdit) {
            setInvalidMessageEdit(true);
            return;
        }

        await updateServiceWorks(getFormattedDateWithHoursAndMinutes(dateTimeFromEdit), getFormattedDateWithHoursAndMinutes(dateTimeToEdit), serviceWorkMessageEdit, id);

        setDateTimeFromEdit(null);
        setDateTimeToEdit(null);
        setServiceWorkMessageEdit('');
        setInvalidDatesEdit(false);
        setInvalidMessageEdit(false);
        setEditedServiceWork(null);
        getServiceWorks();
    };

    const deleteServiceWorks = async (sw: ServiceWorkType) => {
        await removeServiceWorks(sw);
        getServiceWorks();
    };

    const planServiceWorks = async () => {
        setInvalidDates(false);
        if (!(dateTimeFrom && dateTimeTo)) {
            setInvalidDates(true);
            return;
        }

        if (!(dateTimeFrom && dateTimeTo) || dateTimeTo <= dateTimeFrom) {
            setInvalidDates(true);
            return;
        }

        if (!serviceWorkMessage) {
            setInvalidMessage(true);
            return;
        }

        await addServiceWorks(getFormattedDateWithHoursAndMinutes(dateTimeFrom), getFormattedDateWithHoursAndMinutes(dateTimeTo), serviceWorkMessage);

        setDateTimeFrom(null);
        setDateTimeTo(null);
        setServiceWorkMessage('');
        setInvalidDates(false);
        setInvalidMessage(false);
        getServiceWorks();
    };

    return (
        <>
            <h2 className='mb-2 pb-2 pt-2'>Godziny wyłączenia systemu zamówień:</h2>
            <h2 className='mb-5 pb-2 font-weight-bold'>
                od{' '}
                {shutdownHours.from}
                :00 do{' '}
                {shutdownHours.to}
                :00
                {' '}
            </h2>
            <h2 className='mb-4'>Zaplanowane prace serwisowe </h2>
            {isDesktop ? (
                <>
                    <Table borderless striped>
                        <thead style={{ width: '100%' }}>
                            <tr>
                                <td className='col-3'>Od</td>
                                <td className='col-3'>Do</td>
                                <td className='col-6'>Wiadomość</td>
                                <td className='col-1' colSpan={2}>Opcje</td>
                            </tr>
                        </thead>
                        <tbody>
                            {_.map(serviceWorks, (sw) => {
                                const isEditing: boolean = sw === editedServiceWork;
                                return (
                                    <tr key={sw.message}>
                                        <td className='col-3'>{isEditing ? <DatePicker id='dateTimeFromEdit' className='Admin-datetime-input' isClearable isInvalid={invalidDatesEdit} type='datetime' value={dateTimeFromEdit} min={new Date()} onChange={(e) => setDateTimeFromEdit(e)} /> : convertDate(sw.from)}</td>
                                        <td className='col-3'>{isEditing ? <DatePicker id='dateTimeToEdit' className='Admin-datetime-input' isClearable isInvalid={invalidDatesEdit} type='datetime' value={dateTimeToEdit} min={dateTimeFromEdit || new Date()} onChange={(e) => setDateTimeToEdit(e)} /> : convertDate(sw.to)}</td>
                                        <td className='col-6' style={{ width: '4rem' }}>{isEditing ? <Form.Control id='serviceMessageEdit' isInvalid={invalidMessageEdit} value={serviceWorkMessageEdit} onChange={(e) => setServiceWorkMessageEdit(e.target.value)} /> : sw.message}</td>
                                        <td className='col-1' style={{ width: '1rem' }}>
                                            <Button
                                                variant='link'
                                                className='action-button'
                                                disabled={serviceWorkFetching}
                                                onClick={isEditing ? () => editServiceWorks(sw) : () => { setEditedProperties(sw); }}
                                            >
                                                {isEditing ? <FaCheck className={`manage-service-work-icon-check fa-check`} /> : <FaEdit className='manage-service-work-icon' />}
                                            </Button>
                                        </td>
                                        <td className='col-1-2' style={{ width: '1rem' }}>
                                            <Button
                                                variant='link'
                                                className='delete-button'
                                                disabled={serviceWorkFetching}
                                                onClick={() => {
                                                    deleteServiceWorks(sw);
                                                }}
                                            >
                                                <FaRegTrashAlt />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            <tr>
                                <td className='col-3'><DatePicker id='dateTimeFrom' isClearable isInvalid={invalidDates} type='datetime' value={dateTimeFrom} min={new Date()} onChange={(e) => setDateTimeFrom(e)} /></td>
                                <td className='col-3'><DatePicker id='dateTimeTo' isClearable isInvalid={invalidDates} type='datetime' value={dateTimeTo} min={dateTimeFrom || new Date()} onChange={(e) => setDateTimeTo(e)} /></td>
                                <td className='col-6' style={{ width: '4rem' }}><Form.Control id='serviceMessage' isInvalid={invalidMessage} value={serviceWorkMessage} onChange={(e) => setServiceWorkMessage(e.target.value)} /></td>
                                <td className='col-1-2' colSpan={2}>
                                    <Button
                                        onClick={planServiceWorks}
                                        disabled={serviceWorkFetching}
                                    >
                                        <FaPlus />
                                    </Button>
                                </td>
                            </tr>
                        </tbody>

                    </Table>

                </>) :
                <>
                    {
                        _.map(serviceWorks, (sw) => {
                            const isEditing: boolean = sw === editedServiceWork;
                            return (
                                <div key={sw.message} className='mobile-user-block'>
                                    <div className='mobile-user-block-login col-6 '>
                                        <div className='pb-3'>
                                            od:&nbsp;
                                            <b>{isEditing ? <DatePicker id='dateTimeFromEdit' className='Admin-datetime-input' isClearable isInvalid={invalidDatesEdit} type='datetime' value={dateTimeFromEdit} min={new Date(Date.now())} onChange={(e) => setDateTimeFromEdit(e)} /> : convertDate(sw.from)}</b>
                                        </div>
                                        <div>
                                            do:&nbsp;
                                            <b>{isEditing ? <DatePicker id='dateTimeToEdit' className='Admin-datetime-input' isClearable isInvalid={invalidDatesEdit} type='datetime' value={dateTimeToEdit} min={dateTimeFromEdit || new Date(Date.now())} onChange={(e) => setDateTimeToEdit(e)} /> : convertDate(sw.to)}</b>
                                        </div>
                                    </div>
                                    <div className='mobile-user-block-administrator col-6'>{isEditing ? <Form.Control id='serviceMessageEdit' isInvalid={invalidMessageEdit} value={serviceWorkMessageEdit} onChange={(e) => setServiceWorkMessageEdit(e.target.value)} /> : sw.message}</div>
                                    <div>
                                        <Button
                                            variant='link'
                                            className='action-button'
                                            disabled={serviceWorkFetching}
                                            onClick={isEditing ? () => editServiceWorks(sw) : () => { setEditedProperties(sw); }}
                                        >
                                            {isEditing ? <FaCheck className={`manage-service-work-icon fa-check`} /> : <FaEdit className='manage-service-work-icon' />}
                                        </Button>
                                    </div>
                                    <div>
                                        <Button
                                            variant='link'
                                            className='delete-button'
                                            disabled={serviceWorkFetching}
                                            onClick={() => {
                                                deleteServiceWorks(sw);
                                            }}
                                        >
                                            <FaRegTrashAlt />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })
                    }
                    <div className='mobile-user-block'>
                        <div className='mobile-user-block-login col-6 '>
                            <div className='pb-4'>
                                od:
                                {' '}
                                <DatePicker id='dateTimeFrom' className='Admin-datetime-input' isClearable isInvalid={invalidDates} type='datetime' value={dateTimeFrom} min={new Date(Date.now())} onChange={(e) => setDateTimeFrom(e)} />
                            </div>
                            <div>
                                do:
                                {' '}
                                <DatePicker id='dateTimeTo' className='Admin-datetime-input' isClearable isInvalid={invalidDates} type='datetime' value={dateTimeTo} min={dateTimeFrom || new Date(Date.now())} onChange={(e) => setDateTimeTo(e)} />
                            </div>
                        </div>
                        <div className='mobile-user-block-administrator col-6'>
                            <Form.Control id='serviceMessage' isInvalid={invalidMessage} value={serviceWorkMessage} onChange={(e) => setServiceWorkMessage(e.target.value)} />
                        </div>
                    </div>
                    <ButtonComponent
                        onClick={planServiceWorks}
                        disabled={serviceWorkFetching}
                        text='Dodaj prace serwisowe'
                        onHoverAnimation={3}
                    />
                </>}
        </>
    );
};

export default ServiceWorks;
